// MA-837 — the ONE placement-floor rule: only an EVIDENCE floor is a floor.
//
// The mission pick (useTodaysMission), the Journey stone tap (journeyTap
// resolveStoneTap) and /play/foundation's beforeLoad guard all read their
// floor through evidencePlacementFloor below. MA-775 made the mission honour
// only evidence floors (an age seed proves nothing about the child), but the
// tap and the guard kept calling placementFloor() with no provenance, so a
// foundation skill below an AGE-SEEDED reading module was served by Today yet
// unpickable on the Journey and redirected by the guard. Decision + reason:
// docs/build/specs/MA-837-one-floor-rule.md.
//
// Leaf module: imports only the progress layer and the placement-floor
// utility (no React, no react-query, no content packs), so the pure tap and
// the route guard can use it. The diagnostic rung -> module ids below are the
// ladders' own (pinned equal to them in test:ma837-one-floor-rule); the
// ladders themselves import the content reader, which these callers must not.
import { diagnosticResultRung } from "@/engine/diagnosticResultRung";
import {
  recordedModuleSource,
  hasModuleProvenanceEntry,
  ageSeedModules,
  type AgeSeedModules,
  type ModuleStrand,
  type ModuleProvenance,
} from "@/App/progress";
import { placementFloor, isBelowFloor, legacyOrdinal, guardAllowsBelowFloor, type PlacementFloor } from "./placementFloor";
import { FOUNDATION_SKILL_IDS } from "./skillMeta";
import { ageToBand, bandRank } from "@/engine/ageBand";
import microSkillsManifest from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };

// MA-1226 slice 2 (D-1104-28): skill id -> content band (noFailureCheckInFloor).
const SKILL_BAND = new Map((microSkillsManifest as { id: string; ageBand: string }[]).map((m) => [m.id, m.ageBand] as const));


/**
 * P-PATHHOME-BUG-2-ROUND-2 (studentAge=null fix) — resolve the age the
 * age-gate should use on the selection path.
 *
 * progress.studentAge is loaded from localStorage keyed by the LOCAL child id
 * and resolves to `null` (defaultProgress) in several link states — but the
 * child's real age also rides on the registry/kids row the SkillTree already
 * reads. Prefer the persisted progress age; fall back to the registry age so
 * the gate never silently no-ops (null -> ageBandForStudent default "5.5-6.0"
 * -> masks nothing -> age-blind walk hands back chapter 1's lowest "4+1" skill).
 *
 * Pure (no React) so the gate behavior can be pinned in a unit test.
 *
 * MA-80 — this is the ONE age source for every serve/tree/session surface,
 * always called as `resolveSelectionAge(app.progress.studentAge,
 * activeChildAge(app))`: the mission pick below, the four session screens
 * (Math/Reading/Mixed/Foundation: band -> session length, chunk size,
 * auto-read), SkillTree, SkillDetail and useJourneyView (tree/Journey band
 * fit), StoryLibraryScreen, ReadTab, StoryReader, DayCompleteCelebration
 * (reading level / auto-read), onboarding parent-results and the three
 * Parent Settings age reads (band/pacing, level check, auto-read switch).
 * Never
 * read `progress.studentAge` raw (null -> silent "5.5-6.0") or the registry
 * age alone (stale after a cross-device age change, which App.jsx's cloud
 * merge writes to progress only). Pinned per surface, executed, in
 * selectionAgeFallback.test.mjs.
 */
export function resolveSelectionAge(
  progressAge: number | null | undefined,
  registryAge: number | null | undefined,
): number | null {
  if (typeof progressAge === "number" && Number.isFinite(progressAge)) {
    return progressAge;
  }
  if (typeof registryAge === "number" && Number.isFinite(registryAge)) {
    return registryAge;
  }
  return null;
}

/**
 * MA-775 — is this strand's placement floor EVIDENCE (exempt from the age
 * masks and a placement floor for the ranker) rather than an age seed?
 *  1. a provenance entry for the CURRENT module (progress.moduleProvenance,
 *     written by onboarding / the diagnostic / Settings / the level check):
 *     evidence iff its source is not "age";
 *  2. otherwise, the module equals this age's seed (`seeds.own`,
 *     applyPriorBoost(defaultsForAge(age), priors)) → age (the item's
 *     interim rule);
 *  3. the stored onboarding diagnostic (`diagnosticModule`, its terminal
 *     rung's module) chose exactly this module → evidence;
 *  4. NO entry at all (a pre-MA-775 row, or a device that never wrote one)
 *     and the module equals ANY band's seed → age: the legacy Settings
 *     change kept the old age's seed, the midpoint era seeded half a band
 *     high. A STALE entry skips this step — post-MA-775 a module moved by a
 *     writer that records nothing (an engine mastery advance) is evidence;
 *  5. anything else → evidence. Unknown age → evidence (pre-MA-775).
 * `seeds` is plain data from the progress layer (App/progress.ts
 * ageSeedModules, built on the LEAF module engine/ageStartModules.ts) — so
 * neither this module nor the ADR-069 mastery pipelines reach placementEngine
 * or placementValidation, directly or transitively (MA-131 boundary; the
 * reach check is in the MA-775 PR body). Pure; exported for the test.
 */
export function isEvidenceFloor(
  strand: ModuleStrand,
  progress: {
    mathModuleId?: string | null;
    readingModuleId?: string | null;
    moduleProvenance?: ModuleProvenance | null;
  } | null | undefined,
  effectiveAge: number | null | undefined,
  seeds: AgeSeedModules,
  diagnosticModule?: ((strand: ModuleStrand) => string | null | undefined) | null,
): boolean {
  const recorded = recordedModuleSource(progress, strand);
  if (recorded) return recorded !== "age";
  if (typeof effectiveAge !== "number" || !Number.isFinite(effectiveAge) || !seeds.own) return true;
  const current = strand === "math" ? progress?.mathModuleId : progress?.readingModuleId;
  if (!current) return true;
  if (current === (strand === "math" ? seeds.own.mathModuleId : seeds.own.readingModuleId)) return false;
  if (diagnosticModule && diagnosticModule(strand) === current) return true;
  if (!hasModuleProvenanceEntry(progress, strand) && seeds.anyBand[strand].includes(current)) return false;
  return true;
}


/** The terminal-rung -> module id maps of the two onboarding diagnostics
 *  (mathDiagnosticStaircase.MATH_DIAGNOSTIC_STAIRCASE_LADDER and
 *  readingDiagnosticContent.READING_DIAGNOSTIC_LADDER moduleIds, in order). */
export const DIAGNOSTIC_RUNG_MODULE_IDS: Readonly<Record<ModuleStrand, readonly string[]>> = {
  math: ["math.07", "math.09", "math.11", "math.14"],
  reading: ["reading.0a", "reading.0b", "reading.06", "reading.09", "reading.11", "reading.12", "reading.15"],
};

export interface FloorProgressLike {
  placementStatus?: string | null;
  readingModuleId?: string | null;
  mathModuleId?: string | null;
  studentAge?: number | null;
  parentPriors?: Parameters<typeof ageSeedModules>[1];
  moduleProvenance?: ModuleProvenance | null;
  placementResults?: unknown;
  readingPlacementResults?: unknown;
  /** MA-1226 slice 2 (D-1104-35): the check-in facts MA-784's restore carried
   *  from the cloud's placement_results row to a device that holds no stored
   *  result of its own (device-local derived state; see cloudCheckInFromRow). */
  cloudCheckIn?: CloudCheckIns | null;
}

/** MA-1226 slice 2 (D-1104-35): what a fresh device knows of a strand's
 *  check-in, derived from the cloud row MA-784's restore read: the module the
 *  row resolved to, its outcome (true: no failure recorded; false: a failure;
 *  null: the row cannot tell) and its completion time (the result's own
 *  completedAt, the same field the writing device reads; null when absent). */
export interface CloudCheckIn {
  moduleId: string;
  noFailure: boolean | null;
  completedAt: string | null;
  /** the result's accuracy / total (useTodaysMission's start bias reads them) */
  accuracy: number | null;
  total: number | null;
}
export interface CloudCheckIns {
  math?: CloudCheckIn | null;
  reading?: CloudCheckIn | null;
}

/** MA-1226 slice 2 (D-1104-35): the outcome of a stored check-in result, as
 *  storedCheckInNoFailure reads it: true when every item was right (a result
 *  without items: correct >= total, total > 0; else accuracy >= 1 with
 *  total > 0), false when a failure was recorded, null when the result
 *  cannot tell. */
export function checkInOutcome(r: unknown): boolean | null {
  if (!r || typeof r !== "object") return null;
  const x = r as { items?: unknown; correct?: unknown; total?: unknown };
  if (Array.isArray(x.items) && x.items.length > 0) {
    return x.items.every((it) => !!it && typeof it === "object" && (it as { correct?: unknown }).correct === true);
  }
  const correct = Number(x.correct), total = Number(x.total);
  if (Number.isFinite(correct) && Number.isFinite(total) && total > 0) return correct >= total;
  // RED r4 S-e: a result with only accuracy / total (older stored rows) still
  // tells its outcome (accuracy 1: every item right); without it, null.
  const acc = (r as { accuracy?: unknown }).accuracy;
  if (typeof acc === "number" && Number.isFinite(acc) && Number.isFinite(total) && total > 0) return acc >= 1;
  return null;
}

const localCheckIn = (progress: FloorProgressLike | null | undefined, strand: ModuleStrand): unknown =>
  strand === "math" ? progress?.placementResults : progress?.readingPlacementResults;

/** A carried record is usable only with a module id (RED r4 NIT-4: a record
 *  without one is ignored, and MA-784's restore re-reads the cloud). */
export function validCloudCheckIn(c: unknown): CloudCheckIn | null {
  return c && typeof c === "object" && typeof (c as CloudCheckIn).moduleId === "string" ? (c as CloudCheckIn) : null;
}

/** A check-in time as a number; a missing or unreadable time sorts oldest. */
export function checkInTime(at: unknown): number {
  const t = typeof at === "string" ? Date.parse(at) : NaN;
  return Number.isFinite(t) ? t : -Infinity;
}

/** MA-1226 slice 2 (operator D-1104-37, RED r4 MUST-E): per strand the NEWEST
 *  check-in wins, wherever it was run. The candidates are the device's own
 *  stored result and the record MA-784's restore carried from the cloud; the
 *  newer by completedAt is the strand's check-in (a missing time sorts
 *  oldest), and on a tie the device's own result wins (deterministic). Never
 *  "a local result exists". */
export function newestCheckIn(progress: FloorProgressLike | null | undefined, strand: ModuleStrand): (CloudCheckIn & { local: boolean }) | null {
  const raw = localCheckIn(progress, strand);
  const local = raw && typeof raw === "object"
    ? (() => {
        const x = raw as { completedAt?: unknown; accuracy?: unknown; total?: unknown };
        return {
          moduleId: storedDiagnosticModule(progress, strand) as string,
          noFailure: checkInOutcome(raw),
          completedAt: typeof x.completedAt === "string" ? x.completedAt : null,
          accuracy: typeof x.accuracy === "number" ? x.accuracy : null,
          total: typeof x.total === "number" ? x.total : null,
          local: true,
        };
      })()
    : null;
  const c = validCloudCheckIn(progress?.cloudCheckIn?.[strand]);
  const cloud = c ? { ...c, local: false } : null;
  if (!local) return cloud;
  if (!cloud) return local;
  return checkInTime(cloud.completedAt) > checkInTime(local.completedAt) ? cloud : local;
}

/** The newest check-in, only when it resolved to the strand's CURRENT module
 *  (RED r4 NIT-5: an older module's check-in says nothing of this one). */
function currentCheckIn(progress: FloorProgressLike | null | undefined, strand: ModuleStrand): (CloudCheckIn & { local: boolean }) | null {
  const current = strand === "math" ? progress?.mathModuleId : progress?.readingModuleId;
  const c = newestCheckIn(progress, strand);
  return c && current && c.moduleId === current ? c : null;
}

/** MA-1226 slice 2 (D-1104-35, D-1104-37): the check-in time the evidence-floor
 *  rules read (useTodaysMission's evidenceFloorCheckInAt): the newest
 *  check-in's completedAt, only when it resolved to the current module. */
export function checkInAtFor(progress: FloorProgressLike | null | undefined, strand: ModuleStrand): string | null {
  return currentCheckIn(progress, strand)?.completedAt ?? null;
}

/** MA-1226 slice 2 (D-1104-35, D-1104-37): the accuracy / total the
 *  mission's start bias reads (placementFromStoredResult): the newest
 *  check-in's (the device's own stored result object when that is newest). */
export function checkInPlacementResult(
  progress: FloorProgressLike | null | undefined,
  strand: ModuleStrand,
): { accuracy?: number; total?: number } | null {
  const c = newestCheckIn(progress, strand);
  const current = strand === "math" ? progress?.mathModuleId : progress?.readingModuleId;
  // the device's own result as on master; a carried record only for the current module (RED r4 NIT-5)
  if (!c || c.local || c.moduleId !== current) return (localCheckIn(progress, strand) as { accuracy?: number; total?: number } | null | undefined) ?? null;
  return { ...(typeof c.accuracy === "number" ? { accuracy: c.accuracy } : {}), ...(typeof c.total === "number" ? { total: c.total } : {}) };
}

/** MA-1226 slice 2 (D-1104-35; RED r4 S-e: the device's own stored result
 *  too): a check-in for the current module whose outcome cannot be told is
 *  not an evidence floor: the strand keeps the age start. */
function checkInOutcomeUnknown(progress: FloorProgressLike, strand: ModuleStrand): boolean {
  const c = currentCheckIn(progress, strand);
  return !!c && c.noFailure === null;
}

/** The module the stored onboarding diagnostic resolved to for a strand (its
 *  placement rung; a pre-MA-1120 row: its terminal rung), or null. Same clamp as the ladders' own *ModuleIdForRung. */
export function storedDiagnosticModule(
  progress: FloorProgressLike | null | undefined,
  strand: ModuleStrand,
): string | null {
  const r = (strand === "math" ? progress?.placementResults : progress?.readingPlacementResults) as
    | { source?: unknown; finalRung?: unknown; placementRung?: unknown; total?: unknown }
    | null
    | undefined;
  // MA-1120 (RED r1 M1): the PLACEMENT rung, else finalRung (pre-MA-1120 rows)
  const rung = diagnosticResultRung(r);
  if (!r || rung === null || !(Number(r.total) > 0)) return null;
  const want = strand === "math" ? "math_diagnostic_staircase" : "reading_diagnostic_staircase";
  if (r.source !== want) return null;
  const ids = DIAGNOSTIC_RUNG_MODULE_IDS[strand];
  return ids[Math.max(0, Math.min(ids.length - 1, rung))];
}

/**
 * The EVIDENCE floor: the strand's placement floor iff it is evidence, else
 * null. It alone EXEMPTS its skill from the age masks, takes the ranker's
 * floor-skill preference (MA-837) and is the Journey's served stone.
 * `registryAge` is the age fallback (resolveSelectionAge).
 */
export function evidencePlacementFloor(
  strand: ModuleStrand,
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
): PlacementFloor | null {
  if (!progress) return null;
  const age = resolveSelectionAge(progress.studentAge, registryAge);
  const seeds = ageSeedModules(age, progress.parentPriors);
  if (!isEvidenceFloor(strand, progress, age, seeds, (s) => storedDiagnosticModule(progress, s))) return null;
  if (checkInOutcomeUnknown(progress, strand)) return null;
  const floor = placementFloor(strand, progress);
  if (floor && noFailureCheckInFloor(strand, progress, age, floor.skillId)) return null;
  return floor;
}

/**
 * MA-1226 slice 2 (operator D-1104-28) — an ALL-RIGHT check-in is not evidence
 * that the child belongs at or below their band. A staircase that reaches its
 * terminal rung with every answer right ran out of rungs (math.14 -> ms.math.14
 * and reading.15 -> ms.read.15 sit at or below the band of the oldest
 * children): it says nothing against the age model there. Such a result is a
 * floor only when it RAISES the child above their band (its floor skill's band
 * strictly above the child's band: e.g. a 4-year-old placed at ms.math.14, the
 * D-1104-21 rows); at or below the band the strand has NO evidence floor and
 * the age model applies exactly as on master (the mission keeps the result's
 * upward start bias, useTodaysMission). A check-in that recorded a failure is
 * untouched (the staircase stopped on wrong answers: evidence, MA-1120).
 * True here means: no evidence floor. Read side, so rows already stored as
 * "diagnostic" are covered; a strand with no stored staircase result for its
 * current module is untouched (a level check, a Settings change, a mastery move).
 */
export function noFailureCheckInFloor(
  strand: ModuleStrand,
  progress: FloorProgressLike,
  age: number | null,
  floorSkillId: string | null | undefined,
): boolean {
  const current = strand === "math" ? progress.mathModuleId : progress.readingModuleId;
  if (!current) return false;
  // D-1104-37: the strand's NEWEST check-in (own stored result or the record
  // restored from the cloud), for the current module, recorded no failure
  const c = currentCheckIn(progress, strand);
  if (!c || c.noFailure !== true) return false;
  if (typeof age !== "number" || !Number.isFinite(age)) return false;
  const skillBand = floorSkillId ? SKILL_BAND.get(floorSkillId) : undefined;
  if (!skillBand) return false;
  return bandRank(skillBand) <= bandRank(ageToBand(age));
}

/** MA-1226 slice 2 (D-1104-28): the stored check-in result for `strand`
 *  recorded no failure (every item right; a row without items: correct >=
 *  total, total > 0). False with no stored result. */
export function storedCheckInNoFailure(progress: FloorProgressLike | null | undefined, strand: ModuleStrand): boolean {
  return checkInOutcome(localCheckIn(progress, strand)) === true;
}

/**
 * MA-838 — the floor for "what is BELOW it": the evidence floor, else (below-only)
 * the floor of a strand with NO provenance entry at all whose module is a module a
 * diagnostic rung can produce, is a band seed ABOVE this age's own seed, and is not
 * this age's seed. Such a module may be a diagnostic floor whose provenance was
 * lost (fresh device, offline/unlinked onboarding, pre-MA-775 row) or an old age
 * seed a Settings age change kept; they cannot be told apart, so it is honoured
 * ONLY to keep the child out of foundation content below it (the guard, the tap
 * and the mission's below-floor re-rank). A below-only floor (`belowOnly: true`)
 * does NOT exempt its skill from the age masks, does NOT take the floor-skill
 * preference and is NOT the Journey's served stone: an age seed must not act as
 * an evidence floor (MA-775, MA-81 RED F1). One rule for all three consumers.
 */
export function belowFloor(
  strand: ModuleStrand,
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
): PlacementFloor | null {
  const ev = evidencePlacementFloor(strand, progress, registryAge);
  if (ev || !progress) return ev;
  const age = resolveSelectionAge(progress.studentAge, registryAge);
  const seeds = ageSeedModules(age, progress.parentPriors);
  // MA-1226 slice 2 (D-1104-28): an all-right check-in at or below the child's
  // band is not an evidence floor, but the child did answer every item there
  // right, so it stays what it is: a floor for what is BELOW it only (the
  // ranker's below-floor re-rank, the guard, the tap), as on master, never an
  // exemption from the age masks or a served floor.
  const diagFloor = placementFloor(strand, progress);
  if (diagFloor && noFailureCheckInFloor(strand, progress, age, diagFloor.skillId)) return { ...diagFloor, belowOnly: true };
  if (!seeds.own || hasModuleProvenanceEntry(progress, strand)) return null;
  const current = strand === "math" ? progress.mathModuleId : progress.readingModuleId;
  if (!current || !seeds.anyBand[strand].includes(current)) return null;
  if (!DIAGNOSTIC_RUNG_MODULE_IDS[strand].includes(current)) return null;
  const own = strand === "math" ? seeds.own.mathModuleId : seeds.own.readingModuleId;
  const a = legacyOrdinal(strand, current);
  const b = legacyOrdinal(strand, own);
  if (!(a >= 0 && b >= 0 && a > b)) return null;
  const f = placementFloor(strand, progress);
  return f ? { ...f, belowOnly: true } : null;
}

/** The mission's floors, ONE function for useTodaysMission and the tests:
 *  `floors` go to the ranker (below-floor re-rank); `exemptSkillIds` are the
 *  evidence floors' skills only (age-mask exemption, Journey served stone). */
export function missionPlacementFloors(
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
) {
  const floors = { reading: belowFloor("reading", progress, registryAge), math: belowFloor("math", progress, registryAge) };
  const exemptSkillIds = [floors.reading, floors.math]
    .filter((f): f is NonNullable<typeof f> => !!f && !f.belowOnly)
    .map((f) => f.skillId);
  return { floors, exemptSkillIds };
}

/**
 * /play/foundation's beforeLoad decision, pure: a foundation skill strictly
 * below the child's floor (belowFloor) is redirected to the floor skill's own
 * surface; null = let the route load. Same floor as the mission's re-rank and
 * the Journey tap. The route calls this; so does the tests.
 */
export function foundationGuardRedirect(
  skill: string,
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
  // MA-851: the demotion valve of the floor skill is lowered (see placementFloor.guardAllowsBelowFloor)
  valveLowered = false,
): { to: "/play/foundation" | "/play/reading"; skillId: string; floor: PlacementFloor } | null {
  const floor = belowFloor("reading", progress, registryAge);
  if (!floor || !isBelowFloor(skill, floor)) return null;
  if (guardAllowsBelowFloor(skill, floor, valveLowered)) return null;
  return {
    to: FOUNDATION_SKILL_IDS.includes(floor.skillId) ? "/play/foundation" : "/play/reading",
    skillId: floor.skillId,
    floor,
  };
}
